import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Plus,
  Upload,
  Search,
  Clock,
  MapPin,
  Paperclip,
  FileText,
  LayoutGrid,
  List,
  Trash2,
  Pencil,
  RotateCcw,
  AlertCircle,
  Copy,
  TrendingUp,
  Camera,
  Download,
  Star,
  Youtube,
  ChevronDown,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Page, PageHeader, Empty } from "@/components/common";
import ImportDialog from "@/components/ImportDialog";
import { DuplicatesView } from "@/components/DuplicatesView";
import { PopularView } from "@/components/PopularView";
import { MostStarredView } from "@/components/MostStarredView";
import { RecentlyBookedView } from "@/components/RecentlyBookedView";
import { PracticalDocs } from "@/components/PracticalDocs";
import { ManualBookingDialog } from "@/components/ManualBookingDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const SUBJECT_COLOURS = {
  Biology: "text-emerald-600 dark:text-emerald-400",
  Chemistry: "text-sky-600 dark:text-sky-400",
  Physics: "text-amber-600 dark:text-amber-400",
};
const REVIEW_STATUSES = ["Draft", "AI Draft", "Approved"];
const PRIMARY_SCOPE_OPTIONS = [
  { value: "active", label: "All practicals" },
  { value: "approved", label: "Approved templates" },
  { value: "starred", label: "Starred", icon: Star },
  { value: "most-starred", label: "Most starred", icon: Star },
  { value: "recently-booked", label: "Recently booked", icon: Clock },
  { value: "popular", label: "Popular by year", icon: TrendingUp },
];
const MAINTENANCE_SCOPE_OPTIONS = [
  { value: "missing", label: "Missing subject or year", icon: AlertCircle, permission: "edit" },
  { value: "review", label: "Needs review", icon: Camera, permission: "review" },
  { value: "deleted", label: "Recently deleted", icon: Trash2, permission: "edit" },
  { value: "duplicates", label: "Possible duplicates", icon: Copy, permission: "edit" },
];
const linesToText = (items = []) => items
  .map((line) => {
    const amount = line.per_class ? line.qty_fixed : line.qty_per_group;
    const units = line.units ? ` ${line.units}` : "";
    const perClass = line.per_class ? " (per class)" : "";
    return `${line.item} x ${amount}${units}${perClass}`;
  })
  .join("\n");
const blankEquipment = () => ({
  item: "",
  qty: "1",
  units: "",
  per_class: false,
});
const equipmentRow = (line) => {
  if (typeof line === "object" && line) {
    const perClass = Boolean(line.per_class);
    return {
      item: line.item || "",
      qty: String(perClass ? line.qty_fixed ?? 1 : line.qty_per_group ?? 1),
      units: line.units || "",
      per_class: perClass,
    };
  }
  const raw = String(line || "").trim();
  const perClass = /\(per class\)/i.test(raw);
  const cleaned = raw.replace(/\s*\(per class\)\s*/i, "").trim();
  const match = cleaned.match(
    /^(.+?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)\s*(.*)$/i,
  );
  return {
    item: match ? match[1].trim() : cleaned,
    qty: match ? match[2] : "1",
    units: match ? match[3].trim() : "",
    per_class: perClass,
  };
};
const equipmentRows = (items) => {
  const source = Array.isArray(items) ? items : String(items || "").split("\n");
  const rows = source.map(equipmentRow).filter((row) => row.item);
  return rows.length ? rows : [blankEquipment()];
};
const equipmentPayload = (rows) => rows
  .filter((row) => row.item.trim())
  .map((row) => ({
    item: row.item.trim(),
    qty_per_group: row.per_class ? null : Number(row.qty) || 1,
    qty_fixed: row.per_class ? Number(row.qty) || 1 : null,
    units: row.units.trim(),
    per_class: row.per_class,
  }));
const FIELDS = [["name", "Name"], ["subject", "Subject (Biology/Chemistry/Physics)"], ["year_groups", "Year groups (e.g. 8, 9)"], ["key_stage", "Key stage"], ["topic", "Topic"],
  ["risk_assessment_ref", "Risk assessment reference"], ["prep_time_minutes", "Prep time (minutes)"], ["prep_location", "Prep location"], ["group_size", "Pupils per group"], ["version", "Version"], ["review_status", "Review status"], ["youtube_url", "YouTube link"]];
const AREAS = [["description", "Description"], ["chemicals", "Chemicals & materials"], ["technician_notes", "Technician notes"], ["teacher_notes", "Teacher notes"]];

function Editor({ practical, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    ...practical,
    year_groups: (practical.year_groups || []).join(", "),
    equipment: equipmentRows(practical.equipment),
    chemicals: linesToText(practical.chemicals),
  }));
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const updateEquipment = (index, key, value) => {
    setF((current) => ({
      ...current,
      equipment: current.equipment.map((row, rowIndex) => (
        rowIndex === index ? { ...row, [key]: value } : row
      )),
    }));
  };
  const addEquipment = () => {
    setF((current) => ({
      ...current,
      equipment: [...current.equipment, blankEquipment()],
    }));
  };
  const removeEquipment = (index) => {
    setF((current) => ({
      ...current,
      equipment: current.equipment.filter((_, rowIndex) => rowIndex !== index),
    }));
  };
  const save = async () => {
    try {
      const payload = { ...f, equipment: equipmentPayload(f.equipment) };
      const { data } = practical.id
        ? await api.put(`/practicals/${practical.id}`, payload)
        : await api.post("/practicals", payload);
      let failedAttachments = 0;
      for (const attachmentFile of pendingAttachments) {
        const attachment = new FormData();
        attachment.append("file", attachmentFile);
        attachment.append("title", attachmentFile.name);
        attachment.append("category", "Practical instructions");
        attachment.append("practical_id", data.id);
        try {
          await api.post("/documents", attachment);
        } catch {
          failedAttachments += 1;
        }
      }
      toast.success("Practical saved");
      if (failedAttachments) {
        toast.warning(
          `${failedAttachments} attachment${failedAttachments === 1 ? "" : "s"} couldn't be uploaded.`,
        );
      }
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="practical-editor-dialog">
        <DialogHeader>
          <DialogTitle>{practical.id ? "Edit practical" : "New practical"}</DialogTitle>
        </DialogHeader>
        <div>
          <Label className="flex items-center gap-2">
            <Paperclip className="h-4 w-4" />
            Attach documents
          </Label>
          <Input
            type="file"
            multiple
            accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.webp"
            onChange={(event) => setPendingAttachments(Array.from(event.target.files || []))}
            className="mt-1"
            data-testid="practical-editor-attachments"
          />
          {pendingAttachments.length > 0 && (
            <p
              className="mt-1 text-xs text-muted-foreground"
              data-testid="practical-editor-attachment-count"
            >
              {pendingAttachments.length} file{pendingAttachments.length === 1 ? "" : "s"} selected
            </p>
          )}
        </div>
        {practical.id && (
          <PracticalDocs
            practicalId={practical.id}
            canEdit
          />
        )}
        <p
          className="border-l-2 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-950"
          data-testid="practical-finalisation-guidance"
        >
          Before finalising, consult relevant CLEAPSS or SSERC guidance and the school’s own risk
          assessment policy.
        </p>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label>Equipment</Label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addEquipment}
              data-testid="practical-add-equipment-row"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add row
            </Button>
          </div>
          {f.equipment.map((row, index) => (
            <div
              key={`equipment-${index}`}
              className="grid grid-cols-[minmax(0,1fr)_80px_96px_auto] gap-2"
              data-testid={`practical-equipment-row-${index}`}
            >
              <Input
                value={row.item}
                placeholder="Item"
                onChange={(event) => (
                  updateEquipment(index, "item", event.target.value)
                )}
                data-testid={`practical-equipment-item-${index}`}
              />
              <Input
                min="0"
                step="any"
                type="number"
                value={row.qty}
                onChange={(event) => (
                  updateEquipment(index, "qty", event.target.value)
                )}
                data-testid={`practical-equipment-qty-${index}`}
              />
              <Input
                value={row.units}
                placeholder="Units"
                onChange={(event) => (
                  updateEquipment(index, "units", event.target.value)
                )}
                data-testid={`practical-equipment-units-${index}`}
              />
              <div className="flex items-center gap-1">
                <Checkbox
                  checked={row.per_class}
                  onCheckedChange={(value) => (
                    updateEquipment(index, "per_class", Boolean(value))
                  )}
                  aria-label={`Use per-class quantity for ${row.item || "equipment row"}`}
                  data-testid={`practical-equipment-per-class-${index}`}
                />
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  title="Remove equipment row"
                  onClick={() => removeEquipment(index)}
                  data-testid={`practical-equipment-remove-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          <p
            className="text-xs text-muted-foreground"
            data-testid="practical-equipment-row-hint"
          >
            Tick the box beside a row when the quantity applies to the whole class.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label className="text-xs">{label}</Label>
              {key === "review_status" ? (
                <Select
                  value={REVIEW_STATUSES.includes(f[key]) ? f[key] : "Draft"}
                  onValueChange={(value) => setF({ ...f, [key]: value })}
                >
                  <SelectTrigger data-testid="practical-field-review_status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REVIEW_STATUSES.map((status) => (
                      <SelectItem
                        key={status}
                        value={status}
                        data-testid={`practical-review-option-${status.toLowerCase().replace(" ", "-")}`}
                      >
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={key === "youtube_url" ? "url" : "text"}
                  placeholder={key === "youtube_url" ? "https://youtube.com/..." : ""}
                  value={f[key] ?? ""}
                  onChange={(event) => setF({ ...f, [key]: event.target.value })}
                  data-testid={`practical-field-${key}`}
                />
              )}
            </div>
          ))}
        </div>
        {AREAS.map(([k, l]) => <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Textarea rows={2} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`practical-field-${k}`} /></div>)}
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="practical-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Lines = ({ title, items }) => !items?.length ? null : (
  <div><h4 className="text-sm font-semibold mb-1.5">{title}</h4><ul className="text-sm space-y-1">{items.map((l, i) => (
    <li key={i} className="flex justify-between border-b border-dashed pb-1"><span>{l.item}</span><span className="font-mono text-xs text-muted-foreground">{l.per_class ? `${l.qty_fixed} per class` : `${l.qty_per_group} per group`} {l.units}</span></li>))}</ul></div>
);

function Detail({
  p,
  onClose,
  onEdit,
  onDelete,
  onRestore,
  onBook,
  canManage,
  canReview,
  canBook,
}) {
  const canEditDraft = canManage || (canReview && p.review_status !== "Approved");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="practical-detail-dialog">
        <DialogHeader>
          <div className={`text-xs font-semibold uppercase tracking-wider ${SUBJECT_COLOURS[p.subject]}`}>{p.subject} · {p.key_stage} · Year {(p.year_groups || []).join("/")}</div>
          <DialogTitle className="text-xl">{p.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2 text-xs"><Badge variant="outline">{p.topic}</Badge><Badge variant="outline">v{p.version}</Badge><Badge variant="outline">{p.review_status}</Badge></div>
          {p.ai_source === "document" && <p className="text-sm text-muted-foreground" data-testid="practical-ai-document-source">Generated from a document by Ask Jonny AI. Review the extracted details before approving it.</p>}
          {p.ai_source === "screenshot" && <p className="text-sm text-muted-foreground" data-testid="practical-ai-screenshot-source">Generated from a screenshot by Ask Jonny AI. Review the extracted details before approving it.</p>}
          {p.review_status === "Approved" && p.approved_by && <p className="text-sm" data-testid="practical-approved-by"><span className="font-semibold">Approved by: </span>{p.approved_by}{p.approved_at ? ` on ${new Date(p.approved_at).toLocaleDateString("en-GB")}` : ""}</p>}
          {p.description && <p className="text-sm text-muted-foreground">{p.description}</p>}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            {p.prep_time_minutes && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{p.prep_time_minutes} min prep</span>}
            {p.prep_location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.prep_location}</span>}
            {p.risk_assessment_ref && <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" />RA: {p.risk_assessment_ref}</span>}
          {p.youtube_url && <a href={p.youtube_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline" data-testid="practical-youtube-link"><Youtube className="h-3.5 w-3.5" />Open on YouTube</a>}
          </div>
          <Lines title="Equipment" items={p.equipment} />
          <Lines title="Chemicals & materials" items={p.chemicals} />
          {p.technician_notes && <p className="text-sm"><span className="font-semibold">Technician notes: </span>{p.technician_notes}</p>}
          {p.teacher_notes && <p className="text-sm"><span className="font-semibold">Teacher notes: </span>{p.teacher_notes}</p>}
          <PracticalDocs practicalId={p.id} canEdit={canManage && !p.archived} />
        </div>
        {canManage && p.archived && (
          <DialogFooter>
            <Button onClick={onRestore} data-testid="practical-restore-button">
              <RotateCcw className="mr-2 h-4 w-4" />
              Restore
            </Button>
          </DialogFooter>
        )}
        {!p.archived && (canBook || canManage || canEditDraft) && (
          <DialogFooter className="gap-2">
            {canBook && (
              <Button onClick={onBook} data-testid="practical-book-button">
                Book
              </Button>
            )}
            {canManage && (
              <Button
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={onDelete}
                data-testid="practical-delete-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            )}
            {(canManage || canEditDraft) && (
              <Button
                variant="outline"
                onClick={onEdit}
                data-testid="practical-edit-button"
              >
                Edit
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

const yearText = (p) => (p.year_groups || []).join("/");

const KEEP = "__keep";

function BulkField({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={KEEP}>Leave unchanged</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o} data-testid={`${testId}-option-${o.replace(" ", "-")}`}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function BulkEditDialog({ ids, onClose, onDone }) {
  const [f, setF] = useState({ subject: KEEP, key_stage: KEEP, review_status: KEEP, years: "" });
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const years = f.years.split(/[\s,/]+/).filter(Boolean).map(Number);
  const yearsBad = f.years.trim() && years.some((y) => !Number.isInteger(y) || y < 1 || y > 13);
  const changed = f.subject !== KEEP || f.key_stage !== KEEP || f.review_status !== KEEP || (f.years.trim() && !yearsBad);
  const save = async () => {
    const body = { ids };
    ["subject", "key_stage", "review_status"].forEach((k) => { if (f[k] !== KEEP) body[k] = f[k]; });
    if (f.years.trim()) body.year_groups = years;
    try {
      const { data } = await api.post("/practicals/bulk-update", body);
      toast.success(`${data.updated} practical${data.updated === 1 ? "" : "s"} updated`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-edit-dialog">
        <DialogHeader><DialogTitle>Edit {ids.length} practical{ids.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Only the fields you change are updated. Everything else stays as it is.</p>
        <div className="space-y-3">
          <BulkField label="Subject" value={f.subject} onChange={set("subject")} options={["Biology", "Chemistry", "Physics"]} testId="bulk-subject-select" />
          <div className="space-y-1">
            <Label className="text-xs">Year groups (replaces existing, e.g. 7, 8)</Label>
            <Input value={f.years} onChange={(e) => set("years")(e.target.value)} placeholder="Leave blank to keep unchanged" data-testid="bulk-years-input" />
            {yearsBad && <p className="text-xs text-destructive" data-testid="bulk-years-error">Use whole numbers from 1 to 13, separated by commas.</p>}
          </div>
          <BulkField label="Key stage" value={f.key_stage} onChange={set("key_stage")} options={["KS3", "KS4", "KS5"]} testId="bulk-keystage-select" />
          <BulkField label="Review status" value={f.review_status} onChange={set("review_status")} options={REVIEW_STATUSES} testId="bulk-review-select" />
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!changed || yearsBad} data-testid="bulk-edit-save">Apply to {ids.length}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const shortDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

const RestoreBtn = ({ p, onRestore }) => (
  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onRestore(); }} data-testid={`practical-restore-${p.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Restore</Button>
);

function Tile({ p, selectable, checked, onToggle, onOpen, onRestore, onToggleStar }) {
  return (
    <div className={`relative rounded-xl border bg-card hover:border-primary/50 hover:-translate-y-0.5 transition-transform ${checked ? "border-primary ring-1 ring-primary/40" : ""}`}>
      {selectable && (
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          className="absolute top-3 right-11 bg-card"
          aria-label={`Select ${p.name}`}
          data-testid={`practical-select-${p.id}`}
        />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-8 w-8"
        onClick={(event) => {
          event.stopPropagation();
          onToggleStar(p);
        }}
        aria-label={`${p.starred ? "Unstar" : "Star"} ${p.name}`}
        data-testid={`practical-star-${p.id}`}
      >
        <Star
          className={`h-4 w-4 ${
            p.starred ? "fill-amber-400 text-amber-500" : ""
          }`}
        />
      </Button>
      <button onClick={onOpen} className="text-left p-4 w-full" data-testid={`practical-card-${p.id}`}>
        <div className={`text-[11px] font-semibold uppercase tracking-wider pr-6 min-h-4 ${SUBJECT_COLOURS[p.subject] || "text-muted-foreground"}`}>{[p.subject, yearText(p) && `Y${yearText(p)}`].filter(Boolean).join(" · ")}</div>
        <div className="font-semibold mt-1 leading-snug">{p.name}</div>
        <div className="text-xs text-muted-foreground mt-2">{p.topic}</div>
        <div className="flex gap-2 mt-3 text-[11px]"><span className="rounded bg-muted px-1.5 py-0.5">{(p.equipment || []).length} equipment</span>{p.review_status && <span className="rounded bg-muted px-1.5 py-0.5">{p.review_status}</span>}</div>
        {p.archived && <div className="text-[11px] text-muted-foreground mt-2">Deleted {shortDate(p.archived_at)}{p.archived_by ? ` by ${p.archived_by}` : ""}</div>}
      </button>
      {p.archived && <div className="px-4 pb-4"><RestoreBtn p={p} onRestore={onRestore} /></div>}
    </div>
  );
}

function Row({ p, selectable, checked, onToggle, onOpen, onRestore, onToggleStar }) {
  return (
    <tr className={`border-t hover:bg-muted/40 cursor-pointer ${checked ? "bg-primary/5" : ""}`} onClick={onOpen} data-testid={`practical-row-${p.id}`}>
      {selectable && <td className="pl-4 w-10" onClick={(e) => e.stopPropagation()}><Checkbox checked={checked} onCheckedChange={onToggle} aria-label={`Select ${p.name}`} data-testid={`practical-select-${p.id}`} /></td>}
      <td className="p-3 font-medium">
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={(event) => {
              event.stopPropagation();
              onToggleStar(p);
            }}
            aria-label={`${p.starred ? "Unstar" : "Star"} ${p.name}`}
            data-testid={`practical-star-${p.id}`}
          >
            <Star
              className={`h-4 w-4 ${
                p.starred ? "fill-amber-400 text-amber-500" : ""
              }`}
            />
          </Button>
          <span>{p.name}</span>
        </div>
      </td>
      <td className={`p-3 text-xs font-semibold ${SUBJECT_COLOURS[p.subject] || ""}`}>{p.subject}</td>
      <td className="p-3 text-xs">{yearText(p) ? `Y${yearText(p)}` : "—"}</td>
      <td className="p-3 text-xs text-muted-foreground hidden md:table-cell">{p.topic}</td>
      <td className="p-3 text-xs text-muted-foreground hidden lg:table-cell" data-testid={`practical-review-status-${p.id}`}><span className={p.review_status === "AI Draft" ? "font-bold text-foreground" : ""}>{p.review_status}</span></td>
      <td className="p-3 text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap" data-testid={`practical-added-${p.id}`}>{shortDate(p.archived ? p.archived_at : p.created_at)}</td>
      {p.archived && <td className="p-3 text-right"><RestoreBtn p={p} onRestore={onRestore} /></td>}
    </tr>
  );
}

export default function PracticalsPage() {
  const { can } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [layout, setLayout] = useState(() => localStorage.getItem("aj-practicals-view") || "tiles");
  const [layoutPreferenceLoaded, setLayoutPreferenceLoaded] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [bulkEdit, setBulkEdit] = useState(false);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("aj-practicals-sort") || "default");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("all");
  const [scope, setScope] = useState("active");
  const [missingCount, setMissingCount] = useState(0);
  const [view, setView] = useState(null);
  const [edit, setEdit] = useState(null);
  const [imp, setImp] = useState(false);
  const [bookingPractical, setBookingPractical] = useState(null);
  const practicalPrefill = location.state?.practicalPrefill;
  useEffect(() => {
    if (!practicalPrefill || !can("practical:write")) {
      return;
    }
    setEdit(practicalPrefill);
    navigate("/practicals", { replace: true, state: null });
  }, [can, navigate, practicalPrefill]);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/practicals", {
      params: {
        q,
        scope: ["duplicates", "popular", "most-starred", "recently-booked"].includes(scope)
          ? "active"
          : scope,
        subject: subject === "all" ? "" : subject,
      },
    })
      .then((r) => setItems(r.data))
      .catch((error) => toast.error(errMsg(error)))
      .finally(() => setLoading(false));
    api.get("/practicals-missing-count").then((r) => setMissingCount(r.data.count));
  }, [q, subject, scope]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  const canEdit = can("practical:write");
  const canReview = can("practical:review");
  useEffect(() => {
    localStorage.setItem("aj-practicals-view", layout);
    localStorage.setItem("aj-practicals-sort", sortBy);
  }, [layout, sortBy]);
  useEffect(() => {
    api
      .get("/me/preferences")
      .then(({ data }) => {
        if (["tiles", "list"].includes(data.practical_library_view)) {
          setLayout(data.practical_library_view);
        }
      })
      .finally(() => setLayoutPreferenceLoaded(true));
  }, []);
  useEffect(() => {
    if (!layoutPreferenceLoaded) {
      return;
    }
    api.put("/me/preferences", { practical_library_view: layout }).catch(() => undefined);
  }, [layout, layoutPreferenceLoaded]);
  const sorted = sortBy === "default" ? items : [...items].sort((a, b) => {
    if (sortBy === "name") return a.name.localeCompare(b.name);
    const d = (a.created_at || "").localeCompare(b.created_at || "");
    return sortBy === "newest" ? -d : d;
  });
  useEffect(() => {
    setSelected((s) => s.filter((id) => items.some((p) => p.id === id)));
  }, [items]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = items.length > 0 && selected.length === items.length;
  const exportableScope = [
    "active",
    "starred",
    "most-starred",
    "recently-booked",
    "popular",
    "approved",
    "missing",
    "review",
  ].includes(scope);
  const canSelectForExport = can("practical:read") && exportableScope;
  const canSelect = canEdit || canSelectForExport;
  const exportSelected = async () => {
    try {
      const response = await api.post(
        "/practicals/export",
        { ids: selected },
        { responseType: "blob" },
      );
      const downloadUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "ask-jonny-practical-templates.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
      toast.success(
        `${selected.length} practical template${selected.length === 1 ? "" : "s"} downloaded`,
      );
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const doDelete = async () => {
    try {
      const { data } = await api.post("/practicals/bulk-delete", { ids: confirmDel });
      toast.success(`${data.deleted} practical${data.deleted === 1 ? "" : "s"} deleted`);
      setSelected([]);
      setConfirmDel(null);
      setView(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const restore = async (ids) => {
    try {
      const { data } = await api.post("/practicals/bulk-restore", { ids });
      toast.success(`${data.restored} practical${data.restored === 1 ? "" : "s"} restored`);
      setSelected([]);
      setView(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggleStar = async (practical, onDone) => {
    const method = practical.starred ? "delete" : "put";
    setItems((current) => current.map((item) => (
      item.id === practical.id ? { ...item, starred: !practical.starred } : item
    )));
    try {
      await api[method](`/practicals/${practical.id}/star`);
      if (scope === "starred" && practical.starred) {
        setItems((current) => current.filter((item) => item.id !== practical.id));
      }
      onDone?.();
    } catch (error) {
      setItems((current) => current.map((item) => (
        item.id === practical.id ? { ...item, starred: practical.starred } : item
      )));
      toast.error(errMsg(error));
    }
  };
  const showImportedPracticals = () => {
    setSelected([]);
    setQ("");
    setSubject("all");
    setSortBy("newest");
    setScope("active");
    api.get("/practicals", {
      params: {
        q: "",
        scope: "active",
        subject: "",
      },
    }).then((response) => setItems(response.data));
  };
  const itemProps = (p) => ({
    p,
    selectable: canSelect,
    checked: selected.includes(p.id),
    onToggle: () => toggle(p.id),
    onOpen: () => setView(p),
    onRestore: () => restore([p.id]),
    onToggleStar: toggleStar,
  });
  const deletedView = scope === "deleted";
  const countQualifier = deletedView ? "deleted " : scope === "missing" ? "incomplete " : "";
  const countLabel = `${items.length} ${countQualifier}practical${items.length === 1 ? "" : "s"}`;
  const primaryScope = PRIMARY_SCOPE_OPTIONS.some((option) => option.value === scope)
    ? scope
    : "";
  const maintenanceScope = MAINTENANCE_SCOPE_OPTIONS.some((option) => option.value === scope)
    ? scope
    : "";
  const primaryLabel = PRIMARY_SCOPE_OPTIONS.find((option) => option.value === primaryScope)?.label
    || "All practicals";
  const maintenanceLabel = MAINTENANCE_SCOPE_OPTIONS.find(
    (option) => option.value === maintenanceScope,
  )?.label;
  const setLibraryScope = (value) => {
    setScope(value);
    setSelected([]);
  };
  const visibleMaintenanceScopes = MAINTENANCE_SCOPE_OPTIONS.filter((option) => (
    (option.permission === "edit" && canEdit) || (option.permission === "review" && canReview)
  ));

  return (
    <Page wide>
      <PageHeader
        title="Practical library"
        subtitle={(
          <>
            <span data-testid="practical-library-count">{countLabel}</span>
            <span className="block" data-testid="practical-library-ai-booking-note">
              Non-approved practicals do not appear in AI search results and cannot be booked
              directly.
            </span>
          </>
        )}
      />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative min-w-64 flex-1">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search by topic, keyword or name — e.g. osmosis"
            className="pl-9"
            data-testid="library-search-input"
          />
        </div>
        {canEdit && (
          <Button
            variant="outline"
            onClick={() => setImp(true)}
            data-testid="practicals-import-button"
          >
            <Upload className="mr-2 h-4 w-4" />
            Import practicals
          </Button>
        )}
        {canEdit && (
          <Button onClick={() => setEdit({})} data-testid="practicals-new-button">
            <Plus className="mr-2 h-4 w-4" />
            New practical
          </Button>
        )}
        <Tabs value={layout} onValueChange={setLayout}>
          <TabsList>
            <TabsTrigger value="tiles" aria-label="Tile view" data-testid="library-view-tiles">
              <LayoutGrid className="h-4 w-4" />
            </TabsTrigger>
            <TabsTrigger value="list" aria-label="List view" data-testid="library-view-list">
              <List className="h-4 w-4" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="library-scope-bar">
        <Tabs value={subject} onValueChange={setSubject}>
          <TabsList>
            {["all", "Biology", "Chemistry", "Physics"].map((item) => (
              <TabsTrigger
                key={item}
                value={item}
                data-testid={`library-subject-${item}`}
              >
                {item === "all" ? "All" : item}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="sm:w-44" data-testid="library-sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default" data-testid="library-sort-default">
              {q ? "Best match" : "Subject"}
            </SelectItem>
            <SelectItem value="newest" data-testid="library-sort-newest">
              Newest added
            </SelectItem>
            <SelectItem value="oldest" data-testid="library-sort-oldest">
              Oldest added
            </SelectItem>
            <SelectItem value="name" data-testid="library-sort-name">
              Name A–Z
            </SelectItem>
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="min-w-48 justify-between"
              data-testid="library-primary-filter-button"
            >
              {primaryLabel}
              <ChevronDown className="ml-3 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-56">
            <DropdownMenuRadioGroup value={primaryScope} onValueChange={setLibraryScope}>
              {PRIMARY_SCOPE_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <DropdownMenuRadioItem
                    key={option.value}
                    value={option.value}
                    data-testid={`library-scope-${option.value}`}
                  >
                    {Icon && <Icon className="mr-1.5 h-3.5 w-3.5" />}
                    {option.label}
                  </DropdownMenuRadioItem>
                );
              })}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {visibleMaintenanceScopes.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-48 justify-between"
                data-testid="library-maintenance-filter-button"
              >
                {maintenanceLabel ? `Maintenance: ${maintenanceLabel}` : "Maintenance"}
                <ChevronDown className="ml-3 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-56">
              <DropdownMenuLabel>Maintenance</DropdownMenuLabel>
              <DropdownMenuRadioGroup value={maintenanceScope} onValueChange={setLibraryScope}>
                {visibleMaintenanceScopes.map((option) => {
                  const Icon = option.icon;
                  return (
                    <DropdownMenuRadioItem
                      key={option.value}
                      value={option.value}
                      data-testid={`library-scope-${option.value}`}
                    >
                      <Icon className="mr-1.5 h-3.5 w-3.5" />
                      {option.label}
                      {option.value === "missing" && missingCount > 0 && (
                        <span
                          className={
                            "ml-auto rounded-full bg-amber-100 px-1.5 text-[11px] "
                            + "text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                          }
                          data-testid="library-missing-count"
                        >
                          {missingCount}
                        </span>
                      )}
                    </DropdownMenuRadioItem>
                  );
                })}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {scope === "review" && (
          <span className="basis-full text-xs text-muted-foreground">
            Open a draft, check it and set its review status to Approved before it can be booked.
          </span>
        )}
        {scope === "missing" && (
          <span className="basis-full text-xs text-muted-foreground">
            Tick them and use Edit selected to fill in the subject or year groups.
          </span>
        )}
      </div>
      {scope === "popular" ? <PopularView onOpen={(id) => api.get(`/practicals/${id}`).then((r) => setView(r.data))} /> : scope === "most-starred" ? <MostStarredView onOpen={(id) => api.get(`/practicals/${id}`).then((r) => setView(r.data))} onToggleStar={toggleStar} /> : scope === "recently-booked" ? <RecentlyBookedView onOpen={(id) => api.get(`/practicals/${id}`).then((r) => setView(r.data))} onToggleStar={toggleStar} /> : scope === "duplicates" ? <DuplicatesView onChanged={load} /> : (<>
      {canSelect && items.length > 0 && (
        <div className="flex items-center gap-3 mb-3 min-h-9" data-testid="library-selection-bar">
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={allOn} onCheckedChange={() => setSelected(allOn ? [] : items.map((p) => p.id))} data-testid="practical-select-all" />Select all</label>
          {selected.length > 0 && (
            <>
              <span className="text-sm text-muted-foreground" data-testid="library-selected-count">{selected.length} selected</span>
              {!deletedView && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportSelected}
                  data-testid="library-export-selected"
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  Export selected CSV
                </Button>
              )}
              {deletedView && canEdit ? <Button size="sm" onClick={() => restore(selected)} data-testid="library-restore-selected"><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Restore selected</Button> : canEdit && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setBulkEdit(true)} data-testid="library-edit-selected"><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit selected</Button>
                  <Button size="sm" variant="destructive" onClick={() => setConfirmDel(selected)} data-testid="library-delete-selected"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete selected</Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="library-clear-selection">Clear</Button>
            </>
          )}
        </div>
      )}
      {loading ? (
        <div
          className="py-12 text-center text-sm text-muted-foreground"
          data-testid="practical-library-loading"
        >
          Loading practicals…
        </div>
      ) : items.length === 0 ? <Empty>{deletedView ? "Nothing has been deleted." : scope === "missing" ? "Every practical has a subject and year group." : scope === "approved" ? "No approved templates match." : "No practicals match."}</Empty> : layout === "tiles" ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="practicals-list">
          {sorted.map((p) => <Tile key={p.id} {...itemProps(p)} />)}
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="practicals-table">
            <thead><tr className="text-left text-xs text-muted-foreground">{canEdit && <th className="w-10" />}<th className="p-3 font-medium">Name</th><th className="p-3 font-medium">Subject</th><th className="p-3 font-medium">Year</th><th className="p-3 font-medium hidden md:table-cell">Topic</th><th className="p-3 font-medium hidden lg:table-cell">Review</th><th className="p-3 font-medium hidden md:table-cell">{deletedView ? "Deleted" : "Added"}</th>{deletedView && <th />}</tr></thead>
            <tbody>{sorted.map((p) => <Row key={p.id} {...itemProps(p)} />)}</tbody>
          </table>
        </div>
      )}
      </>)}
      {view && <Detail p={view} canManage={canEdit} canReview={canReview} canBook={can("booking:create") && view.review_status === "Approved"} onClose={() => setView(null)} onEdit={() => { setEdit(view); setView(null); }} onDelete={() => setConfirmDel([view.id])} onRestore={() => restore([view.id])} onBook={() => { setBookingPractical(view); setView(null); }} />}
      {bulkEdit && <BulkEditDialog ids={selected} onClose={() => setBulkEdit(false)} onDone={() => { setBulkEdit(false); load(); }} />}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="library-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDel?.length === 1 ? "this practical" : `${confirmDel?.length} practicals`}?</AlertDialogTitle>
            <AlertDialogDescription>They'll be removed from the library and Jonny won't offer them for new bookings. Existing bookings keep their details.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="library-delete-cancel">Keep</AlertDialogCancel><AlertDialogAction onClick={doDelete} data-testid="library-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {edit && <Editor practical={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      <ImportDialog
        kind="practicals"
        open={imp}
        onOpenChange={setImp}
        onDone={showImportedPracticals}
      />
      <ManualBookingDialog
        open={Boolean(bookingPractical)}
        initialPractical={bookingPractical}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setBookingPractical(null);
        }}
      />
    </Page>
  );
}
